import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Image as HeroImage,
  Input,
  Progress,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Tabs,
  Textarea,
  useDisclosure,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  copyToClipboard,
  getPreviewPageUrl,
  resolveGifUrl,
} from '../../gifs/services'
import { QueueService } from '../../gifs/services/queueService'
import { QuickAddModal } from '../../gifs/components/QuickAddModal'
import { useIncomingGifs } from '../hooks/useIncomingGifs'
import type { GifDocument } from '../../../types'
import {
  isRecordApproved,
  isRecordStale,
} from '../../../../functions/src/shared/types/retrieval'
import type {
  ExplicitRating,
  GifReference,
} from '../../../../functions/src/shared/types/gif'
import type { RetrievalManualEdits } from '../services/retrievalReviewApi'
import {
  applyManualEdits,
  approveRetrievalRecord,
  listRetrievalReview,
  reanalyzeWithHints,
  type DuplicateCandidate,
  type DuplicateReason,
  type RetrievalReviewCounts,
  type RetrievalReviewFilter,
  type RetrievalReviewItem,
} from '../services/retrievalReviewApi'

type FilterTab = RetrievalReviewFilter

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'unreviewed', label: 'Needs review' },
  { key: 'approved', label: 'Approved' },
  { key: 'all', label: 'All' },
]

/** Render the animated GIF for a review item (resolves the storage url). */
function ReviewGif({
  item,
  className,
}: {
  item: RetrievalReviewItem
  className?: string
}) {
  const [url, setUrl] = useState('')
  const [failed, setFailed] = useState(false)
  const gifUrl = item.gif?.gifUrl ?? null

  useEffect(() => {
    let alive = true
    setFailed(false)
    setUrl('')
    if (!gifUrl) {
      setFailed(true)
      return
    }
    resolveGifUrl(gifUrl)
      .then((u) => {
        if (alive) setUrl(u)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })
    return () => {
      alive = false
    }
  }, [gifUrl])

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center bg-default-100 rounded-lg text-xs text-default-400 ${className}`}
      >
        no preview
      </div>
    )
  }
  if (!url) {
    return (
      <div
        className={`flex items-center justify-center bg-default-100 rounded-lg ${className}`}
      >
        <Spinner size="sm" />
      </div>
    )
  }
  return (
    <HeroImage
      src={url}
      alt={item.gif?.title ?? item.gifId}
      radius="lg"
      className={`object-contain ${className}`}
      classNames={{ wrapper: className, img: 'object-contain' }}
    />
  )
}

/** Human-facing label + color for each duplicate-detection reason. */
const DUPLICATE_REASON_META: Record<
  DuplicateReason,
  { label: string; color: 'danger' | 'warning' }
> = {
  'exact-input-hash': { label: 'identical file', color: 'danger' },
  'exact-output-hash': { label: 'identical encoded file', color: 'danger' },
  'same-platform-id': { label: 'same source post', color: 'danger' },
  'same-source-url': { label: 'same source URL', color: 'warning' },
  semantic: { label: 'very similar content', color: 'warning' },
}

/** Small animated thumbnail for a duplicate candidate (resolves the url). */
function CandidateThumb({
  gifUrl,
  alt,
}: {
  gifUrl: string | null
  alt: string
}) {
  const [url, setUrl] = useState('')
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    setFailed(false)
    setUrl('')
    if (!gifUrl) {
      setFailed(true)
      return
    }
    resolveGifUrl(gifUrl)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
  }, [gifUrl])
  if (failed)
    return (
      <div className="w-14 h-14 rounded bg-default-100 flex items-center justify-center text-[9px] text-default-400">
        no preview
      </div>
    )
  if (!url)
    return (
      <div className="w-14 h-14 rounded bg-default-100 flex items-center justify-center">
        <Spinner size="sm" />
      </div>
    )
  return (
    <HeroImage
      src={url}
      alt={alt}
      radius="sm"
      classNames={{ wrapper: 'w-14 h-14', img: 'w-14 h-14 object-cover' }}
    />
  )
}

/**
 * Warning banner listing collection GIFs that look like duplicates of the
 * active record. No dismiss state — approving the record is the implicit
 * dismissal. "View" jumps focus to the candidate; the preview link opens the
 * candidate's shareable page in a new tab.
 */
function DuplicateWarningCard({
  candidates,
  onView,
}: {
  candidates: DuplicateCandidate[]
  onView: (gifId: string) => void
}) {
  return (
    <Card className="border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-950/20">
      <CardHeader className="pb-1">
        <h3 className="text-sm font-semibold text-warning-700 dark:text-warning-300">
          ⚠ Possible duplicate — {candidates.length} similar GIF
          {candidates.length === 1 ? '' : 's'} already in the collection
        </h3>
      </CardHeader>
      <CardBody className="space-y-2">
        {candidates.map((candidate) => (
          <div
            key={candidate.gifId}
            className="flex items-center gap-3 p-2 rounded-lg bg-background/60 dark:bg-default-100/10"
          >
            <div className="w-14 h-14 flex-shrink-0">
              <CandidateThumb
                gifUrl={candidate.gifUrl}
                alt={candidate.title ?? candidate.gifId}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium line-clamp-1">
                {candidate.title ?? candidate.gifId}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {candidate.reasons.map((reason) => {
                  const meta = DUPLICATE_REASON_META[reason]
                  const label =
                    reason === 'semantic'
                      ? `${meta.label} · ${Math.round(candidate.score * 100)}%`
                      : meta.label
                  return (
                    <Chip
                      key={reason}
                      size="sm"
                      variant="flat"
                      color={meta.color}
                    >
                      {label}
                    </Chip>
                  )
                })}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                size="sm"
                variant="flat"
                color="warning"
                onPress={() => onView(candidate.gifId)}
              >
                View
              </Button>
              {candidate.gifUrl && (
                <Button
                  as="a"
                  size="sm"
                  variant="light"
                  href={getPreviewPageUrl(candidate.gifUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Preview ↗
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardBody>
    </Card>
  )
}

/** A labeled list of strings (scenario examples, exclusions, etc.). */
function FieldList({ title, items }: { title: string; items: string[] }) {
  if (!items || items.length === 0) return null
  return (
    <div>
      <div className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
        {title}
      </div>
      <ul className="list-disc list-inside space-y-0.5 text-sm">
        {items.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ul>
    </div>
  )
}

function statusChip(item: RetrievalReviewItem) {
  if (isRecordApproved(item)) {
    return (
      <Chip size="sm" color="success" variant="flat">
        approved
      </Chip>
    )
  }
  if (isRecordStale(item)) {
    return (
      <Chip size="sm" color="warning" variant="flat">
        stale — re-analyzed since approval
      </Chip>
    )
  }
  return (
    <Chip size="sm" color="default" variant="flat">
      needs review
    </Chip>
  )
}

const EDITABLE_TONES = ['sincere', 'sarcastic', 'both'] as const
const EDITABLE_PERSPECTIVES = ['User Voice', 'Mimicry', 'both'] as const

const RATING_OPTIONS = ['G', 'PG', 'PG-13', 'R'] as ExplicitRating[]

/**
 * The confirmed catalog values the curator edits on the card, in precedence
 * order: the gifs-doc's current explicitRating/reference (the confirmed truth
 * for existing gifs) → the record's proposedMeta (AI proposal for new gifs) →
 * empty. Approve always sends these as `confirmed`.
 */
interface ConfirmedMeta {
  explicitRating: ExplicitRating | ''
  source: string
  keywords: string[]
  year: string // text-input backed; parsed to number|null on approve
}

function seedConfirmedMeta(item: RetrievalReviewItem | null): ConfirmedMeta {
  if (!item) return { explicitRating: '', source: '', keywords: [], year: '' }
  const rating =
    item.gif?.explicitRating ?? item.proposedMeta?.explicitRating ?? ''
  const ref: GifReference | undefined =
    item.gif?.reference ?? item.proposedMeta?.reference
  return {
    explicitRating: rating,
    source: ref?.source ?? '',
    keywords: ref?.keywords ?? [],
    year: ref?.year != null ? String(ref.year) : '',
  }
}

/** Whether a record has ever been published (badge gate). */
function isUnpublished(item: RetrievalReviewItem): boolean {
  return !item.publishedAt && !item.gif?.publishedAt
}

/** Small tag input backed by an array of strings. */
function KeywordsInput({
  value,
  onChange,
}: {
  value: string[]
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const t = draft.trim()
    if (t && !value.includes(t)) onChange([...value, t])
    setDraft('')
  }
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {value.map((kw) => (
          <Chip
            key={kw}
            size="sm"
            variant="flat"
            onClose={() => onChange(value.filter((k) => k !== kw))}
          >
            {kw}
          </Chip>
        ))}
      </div>
      <Input
        size="sm"
        aria-label="Add keyword"
        placeholder="Add keyword + Enter"
        value={draft}
        onValueChange={setDraft}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add()
          }
        }}
        onBlur={add}
      />
    </div>
  )
}

export function RetrievalReviewPage() {
  const toast = useToast()

  // Deep-link: `?gif=<id>` focuses a specific record after the list loads.
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkGif = searchParams.get('gif')

  const [filter, setFilter] = useState<FilterTab>('unreviewed')
  const [searchText, setSearchText] = useState('')
  const [items, setItems] = useState<RetrievalReviewItem[]>([])
  const [counts, setCounts] = useState<RetrievalReviewCounts>({
    total: 0,
    approved: 0,
    unreviewed: 0,
  })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [activeId, setActiveId] = useState<string | null>(null)

  // Deep-link focus: the gif to briefly highlight in the queue, plus refs to
  // each queue button so we can scroll the target into view once it renders.
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const queueButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  // Tracks which `?gif=` value we've already resolved, so switching filters or
  // re-loading the queue doesn't re-trigger the jump.
  const resolvedDeepLink = useRef<string | null>(null)

  // Per-card interaction state.
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [fixOpen, setFixOpen] = useState(false)
  const [hintDraft, setHintDraft] = useState('')
  const [editOpen, setEditOpen] = useState(false)
  const [edits, setEdits] = useState<RetrievalManualEdits>({})
  // The record as it was before a re-analysis, for side-by-side comparison.
  const [previous, setPrevious] = useState<RetrievalReviewItem | null>(null)

  // Confirmed catalog values (rating + reference chips) for the active card.
  const [confirmed, setConfirmed] = useState<ConfirmedMeta>(() =>
    seedConfirmedMeta(null)
  )

  // Incoming ingestion queue (pending/analyzing/failed) + Add GIF modal.
  const { incoming, error: incomingError } = useIncomingGifs()
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const addModal = useDisclosure()

  // Which filter the currently-displayed `items` were loaded FOR. Loads can
  // overlap (mount kicks off the default filter, then a `?gif=` deep link
  // immediately switches to `all`) — a stale response must neither clobber the
  // newer one nor fool the deep-link focus effect into searching the wrong list.
  const [loadedFilter, setLoadedFilter] = useState<FilterTab | null>(null)
  const loadSeqRef = useRef(0)

  const loadQueue = useCallback(
    async (nextFilter: FilterTab, preserveActive = false) => {
      const seq = ++loadSeqRef.current
      setLoading(true)
      setLoadError(null)
      try {
        const { items: next, counts: nextCounts } =
          await listRetrievalReview(nextFilter)
        if (seq !== loadSeqRef.current) return // superseded by a newer load
        setItems(next)
        setCounts(nextCounts)
        setLoadedFilter(nextFilter)
        setActiveId((cur) => {
          if (preserveActive && cur && next.some((i) => i.gifId === cur)) {
            return cur
          }
          return next[0]?.gifId ?? null
        })
      } catch (err) {
        if (seq !== loadSeqRef.current) return
        const msg = err instanceof Error ? err.message : String(err)
        setLoadError(msg)
      } finally {
        if (seq === loadSeqRef.current) setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    void loadQueue(filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  // A deep-linked gif may be in any state (e.g. already approved), which the
  // default "needs review" filter would hide. Force the "all" filter on mount
  // so it's guaranteed loadable; do it once per distinct `?gif=` value.
  useEffect(() => {
    if (deepLinkGif && resolvedDeepLink.current !== deepLinkGif) {
      setFilter('all')
    }
  }, [deepLinkGif])

  // Once the queue has loaded, focus the deep-linked gif: select it, scroll its
  // queue thumbnail into view, and flash a highlight. Toast if it's not found.
  useEffect(() => {
    if (!deepLinkGif || loading) return
    if (resolvedDeepLink.current === deepLinkGif) return
    // Wait until the displayed items are genuinely the "all" load — `filter`
    // alone isn't enough, because the mount-time default-filter load can
    // resolve after the switch and would otherwise be searched instead.
    if (filter !== 'all' || loadedFilter !== 'all') return
    resolvedDeepLink.current = deepLinkGif

    const target = items.find((i) => i.gifId === deepLinkGif)
    if (target) {
      setActiveId(deepLinkGif)
      setHighlightId(deepLinkGif)
      // Defer the scroll until the button ref is populated for this render.
      requestAnimationFrame(() => {
        queueButtonRefs.current.get(deepLinkGif)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      })
      const t = setTimeout(() => setHighlightId(null), 2000)
      // Consume the param so a refresh/back doesn't re-jump.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('gif')
          return next
        },
        { replace: true }
      )
      return () => clearTimeout(t)
    }

    toast.error(`GIF ${deepLinkGif} isn't in the retrieval review queue`)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('gif')
        return next
      },
      { replace: true }
    )
  }, [
    deepLinkGif,
    loading,
    filter,
    loadedFilter,
    items,
    setSearchParams,
    toast,
  ])

  // Text search over the loaded queue: title, reference source/keywords,
  // narrative, breadth tags, and the raw gif id all count as matches.
  const visibleItems = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    if (!q) return items
    return items.filter((it) => {
      const haystack = [
        it.gifId,
        it.gif?.title,
        it.gif?.reference?.source,
        ...(it.gif?.reference?.keywords ?? []),
        it.narrative,
        ...(it.breadthTags ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [items, searchText])

  const active = useMemo(
    () => items.find((i) => i.gifId === activeId) ?? null,
    [items, activeId]
  )

  const activeIndex = useMemo(
    () => items.findIndex((i) => i.gifId === activeId),
    [items, activeId]
  )

  // Reset per-card panels whenever the SELECTED record changes. Intentionally
  // keyed on activeId only: re-running when `active` re-renders (e.g. after a
  // patch from re-analysis) would clobber the just-populated comparison + draft.
  useEffect(() => {
    setFixOpen(false)
    setEditOpen(false)
    setPrevious(null)
    setHintDraft(active?.curatorHints ?? '')
    setEdits({})
    setConfirmed(seedConfirmedMeta(active))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  // Advance within the search-filtered view, so working through matches
  // doesn't jump back out to non-matching records.
  const advance = useCallback(() => {
    if (visibleItems.length === 0) return
    const idx = visibleItems.findIndex((i) => i.gifId === activeId)
    const next = visibleItems[idx + 1] ?? null
    setActiveId(next ? next.gifId : null)
  }, [visibleItems, activeId])

  const patchItem = useCallback((updated: RetrievalReviewItem) => {
    setItems((prev) =>
      prev.map((i) => (i.gifId === updated.gifId ? updated : i))
    )
  }, [])

  const handleApprove = useCallback(async () => {
    if (!active || busy) return
    setBusy(true)
    try {
      // Always send the current chip values as confirmed catalog fields.
      const trimmedYear = confirmed.year.trim()
      const parsedYear = trimmedYear ? Number(trimmedYear) : null
      const reference: GifReference = {
        source: confirmed.source.trim() || null,
        keywords: confirmed.keywords,
        year:
          parsedYear != null && Number.isFinite(parsedYear) ? parsedYear : null,
      }
      const updated = await approveRetrievalRecord(active.gifId, {
        ...(confirmed.explicitRating
          ? { explicitRating: confirmed.explicitRating }
          : {}),
        reference,
      })
      patchItem(updated)
      setCounts((c) => ({
        ...c,
        approved: c.approved + (isRecordApproved(active) ? 0 : 1),
        unreviewed: c.unreviewed - (isRecordApproved(active) ? 0 : 1),
      }))
      toast.success('Approved')
      // In the "needs review" filter the approved item leaves the queue.
      if (filter === 'unreviewed') {
        setItems((prev) => {
          const remaining = prev.filter((i) => i.gifId !== active.gifId)
          setActiveId((cur) =>
            cur === active.gifId
              ? (remaining[activeIndex]?.gifId ?? remaining[0]?.gifId ?? null)
              : cur
          )
          return remaining
        })
      } else {
        advance()
      }
    } catch (err) {
      toast.error(
        `Approve failed: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      setBusy(false)
    }
  }, [active, busy, confirmed, filter, activeIndex, advance, patchItem, toast])

  const handleSkip = useCallback(() => {
    advance()
  }, [advance])

  const handleRetry = useCallback(
    async (gifId: string) => {
      setRetryingId(gifId)
      try {
        await QueueService.retryAnalysis(gifId)
        toast.success('Analysis re-queued')
        // The record surfaces in the review queue once analysis completes;
        // refresh so it appears without a manual reload.
        void loadQueue(filter, true)
      } catch (err) {
        toast.error(
          `Retry failed: ${err instanceof Error ? err.message : String(err)}`
        )
      } finally {
        setRetryingId(null)
      }
    },
    [toast, loadQueue, filter]
  )

  const handleAddSuccess = useCallback(() => {
    // A newly-queued gif analyzes in the background; reload so its record shows
    // up in the review queue when it completes.
    void loadQueue(filter, true)
  }, [loadQueue, filter])

  const handleSubmitHints = useCallback(async () => {
    if (!active || busy) return
    setBusy(true)
    const before = active
    try {
      const updated = await reanalyzeWithHints(active.gifId, hintDraft)
      setPrevious(before)
      patchItem(updated)
      setFixOpen(false)
      toast.success('Re-analyzed — review the new record and approve')
    } catch (err) {
      toast.error(
        `Re-analysis failed: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      setBusy(false)
    }
  }, [active, busy, hintDraft, patchItem, toast])

  const handleSubmitEdits = useCallback(async () => {
    if (!active || busy) return
    if (Object.keys(edits).length === 0) {
      toast.error('No field edits to save')
      return
    }
    setBusy(true)
    const before = active
    try {
      const updated = await applyManualEdits(active.gifId, edits)
      setPrevious(before)
      patchItem(updated)
      setEditOpen(false)
      setEdits({})
      toast.success('Saved edits — embedding rebuilt. Approve when satisfied.')
    } catch (err) {
      toast.error(
        `Edit failed: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      setBusy(false)
    }
  }, [active, busy, edits, patchItem, toast])

  const handleCopyLink = useCallback(async () => {
    const url = active?.gif?.gifUrl
    if (!url) {
      toast.error('This gif has no URL to copy')
      return
    }
    const ok = await copyToClipboard(getPreviewPageUrl(url))
    if (ok) toast.success('Link copied to clipboard!')
    else toast.error('Failed to copy link to clipboard')
  }, [active, toast])

  // Delete the gif entirely (gifs doc + gifRetrieval record + Storage binaries),
  // server-side. An explicit, confirmed curator action — never weaken the
  // confirm. On success the item leaves the queue and selection advances.
  const handleDelete = useCallback(async () => {
    if (!active || busy || deleting) return
    const title = active.gif?.title ?? active.gifId
    const source = active.gif?.reference?.source || 'Unknown'
    const confirmed = window.confirm(
      `Are you sure you want to delete this GIF?\n\n` +
        `Description: ${title}\nSource: ${source}\n\n` +
        `This removes the gif, its retrieval record, and its media files. ` +
        `This action cannot be undone.`
    )
    if (!confirmed) return

    setDeleting(true)
    const wasApproved = isRecordApproved(active)
    try {
      await QueueService.deleteGifCompletely(active.gifId)
      // Advance selection before dropping the item so the next card is focused.
      advance()
      setItems((prev) => prev.filter((i) => i.gifId !== active.gifId))
      setCounts((c) => ({
        total: Math.max(0, c.total - 1),
        approved: Math.max(0, c.approved - (wasApproved ? 1 : 0)),
        unreviewed: Math.max(0, c.unreviewed - (wasApproved ? 0 : 1)),
      }))
      toast.success('GIF deleted')
    } catch (err) {
      toast.error(
        `Delete failed: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      setDeleting(false)
    }
  }, [active, busy, deleting, advance, toast])

  // Keyboard shortcuts: a = approve, s = skip. Disabled while a text panel is
  // open (so typing hints/edits doesn't trigger them).
  const panelOpen = fixOpen || editOpen
  const panelOpenRef = useRef(panelOpen)
  panelOpenRef.current = panelOpen
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (panelOpenRef.current) return
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return
      }
      if (e.key === 'a') {
        e.preventDefault()
        void handleApprove()
      } else if (e.key === 's') {
        e.preventDefault()
        handleSkip()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleApprove, handleSkip])

  const openEdit = () => {
    if (!active) return
    setEdits({
      narrative: active.narrative,
      scenarioExamples: active.scenarioExamples,
      exclusionConditions: active.exclusionConditions,
      distinctiveFeatures: active.distinctiveFeatures,
      specificityScore: active.specificityScore,
      breadthTags: active.breadthTags,
      toneAffordances: active.toneAffordances,
      perspectiveAffordances: active.perspectiveAffordances,
    })
    setFixOpen(false)
    setEditOpen(true)
  }

  const renderRecordBody = (rec: RetrievalReviewItem) => (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
          Narrative
        </div>
        <p className="text-sm">{rec.narrative}</p>
      </div>
      <FieldList title="Scenario examples" items={rec.scenarioExamples} />
      <FieldList title="Exclusion conditions" items={rec.exclusionConditions} />
      <FieldList title="Distinctive features" items={rec.distinctiveFeatures} />
      <div className="flex flex-wrap gap-4 items-start">
        <div>
          <div className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
            Specificity
          </div>
          <Chip size="sm" variant="flat" color="primary">
            {rec.specificityScore} / 5
          </Chip>
        </div>
        <div>
          <div className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
            Tone
          </div>
          <Chip size="sm" variant="flat">
            {rec.toneAffordances}
          </Chip>
        </div>
        <div>
          <div className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
            Perspective
          </div>
          <Chip size="sm" variant="flat">
            {rec.perspectiveAffordances}
          </Chip>
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
          Breadth tags
        </div>
        <div className="flex flex-wrap gap-1">
          {rec.breadthTags.map((t) => (
            <Chip key={t} size="sm" variant="bordered">
              {t}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  )

  const renderFixPanel = () => (
    <Card className="border-2 border-primary-200 dark:border-primary-800">
      <CardHeader className="pb-1">
        <h3 className="text-sm font-semibold">
          Fix with a hint — the model re-analyzes with your correction
        </h3>
      </CardHeader>
      <CardBody className="space-y-3">
        <Textarea
          aria-label="Curator hint"
          placeholder="e.g. This is a sarcastic slow clap, not sincere applause. The character is mocking, not celebrating."
          value={hintDraft}
          onChange={(e) => setHintDraft(e.target.value)}
          minRows={3}
          description="Persistent — this correction is re-injected into every future re-analysis. Replace semantics: the full text you enter becomes the record's curatorHints."
        />
        <div className="flex gap-2">
          <Button
            color="primary"
            isLoading={busy}
            onPress={() => void handleSubmitHints()}
          >
            {busy ? 'Re-analyzing (15–60s)…' : 'Re-analyze with hint'}
          </Button>
          <Button
            variant="flat"
            isDisabled={busy}
            onPress={() => setFixOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  )

  const renderEditPanel = () => (
    <Card className="border-2 border-secondary-200 dark:border-secondary-800">
      <CardHeader className="pb-1">
        <h3 className="text-sm font-semibold">
          Edit fields directly (fallback) — embedding is rebuilt on save
        </h3>
      </CardHeader>
      <CardBody className="space-y-3">
        <Textarea
          label="Narrative"
          value={edits.narrative ?? ''}
          onChange={(e) =>
            setEdits((p) => ({ ...p, narrative: e.target.value }))
          }
          minRows={3}
        />
        <Textarea
          label="Scenario examples (one per line)"
          value={(edits.scenarioExamples ?? []).join('\n')}
          onChange={(e) =>
            setEdits((p) => ({
              ...p,
              scenarioExamples: splitLines(e.target.value),
            }))
          }
          minRows={3}
        />
        <Textarea
          label="Exclusion conditions (one per line)"
          value={(edits.exclusionConditions ?? []).join('\n')}
          onChange={(e) =>
            setEdits((p) => ({
              ...p,
              exclusionConditions: splitLines(e.target.value),
            }))
          }
          minRows={2}
        />
        <Textarea
          label="Distinctive features (one per line)"
          value={(edits.distinctiveFeatures ?? []).join('\n')}
          onChange={(e) =>
            setEdits((p) => ({
              ...p,
              distinctiveFeatures: splitLines(e.target.value),
            }))
          }
          minRows={2}
        />
        <Textarea
          label="Breadth tags (comma or newline separated)"
          value={(edits.breadthTags ?? []).join(', ')}
          onChange={(e) =>
            setEdits((p) => ({
              ...p,
              breadthTags: e.target.value
                .split(/[,\n]/)
                .map((s) => s.trim())
                .filter(Boolean),
            }))
          }
          minRows={2}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="Specificity"
            selectedKeys={
              edits.specificityScore ? [String(edits.specificityScore)] : []
            }
            onSelectionChange={(keys) => {
              const v = Number(Array.from(keys)[0])
              if (v >= 1 && v <= 5) {
                setEdits((p) => ({
                  ...p,
                  specificityScore:
                    v as RetrievalManualEdits['specificityScore'],
                }))
              }
            }}
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <SelectItem key={String(n)}>{String(n)}</SelectItem>
            ))}
          </Select>
          <Select
            label="Tone"
            selectedKeys={edits.toneAffordances ? [edits.toneAffordances] : []}
            onSelectionChange={(keys) => {
              const v = Array.from(keys)[0] as string
              setEdits((p) => ({
                ...p,
                toneAffordances: v as RetrievalManualEdits['toneAffordances'],
              }))
            }}
          >
            {EDITABLE_TONES.map((t) => (
              <SelectItem key={t}>{t}</SelectItem>
            ))}
          </Select>
          <Select
            label="Perspective"
            selectedKeys={
              edits.perspectiveAffordances ? [edits.perspectiveAffordances] : []
            }
            onSelectionChange={(keys) => {
              const v = Array.from(keys)[0] as string
              setEdits((p) => ({
                ...p,
                perspectiveAffordances:
                  v as RetrievalManualEdits['perspectiveAffordances'],
              }))
            }}
          >
            {EDITABLE_PERSPECTIVES.map((t) => (
              <SelectItem key={t}>{t}</SelectItem>
            ))}
          </Select>
        </div>
        <div className="flex gap-2">
          <Button
            color="secondary"
            isLoading={busy}
            onPress={() => void handleSubmitEdits()}
          >
            Save edits (rebuild embedding)
          </Button>
          <Button
            variant="flat"
            isDisabled={busy}
            onPress={() => setEditOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  )

  const progressValue =
    counts.total > 0 ? (counts.approved / counts.total) * 100 : 0

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Curation</h1>
          <p className="text-sm text-default-500 mt-1">
            Add a GIF, watch it analyze in the Incoming section, then review the
            AI-facing retrieval record. Approve accurate ones (confirming the
            rating + reference chips); fix wrong ones with a hint or edit fields
            directly. Approval publishes the gif (searchable + recommendable).
          </p>
        </div>
        <Button color="primary" onPress={addModal.onOpen}>
          + Add GIF
        </Button>
      </div>

      {/* Incoming ingestion queue: pending / analyzing / failed */}
      <IncomingSection
        incoming={incoming}
        error={incomingError}
        retryingId={retryingId}
        onRetry={handleRetry}
      />

      {/* Progress + filter tabs */}
      <Card>
        <CardBody className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium">
              {counts.approved} of {counts.total} approved
            </span>
            <span className="text-xs text-default-500">
              {counts.unreviewed} need review
            </span>
          </div>
          <Progress
            aria-label="Approval progress"
            value={progressValue}
            color="success"
            size="sm"
          />
          <Tabs
            aria-label="Review filter"
            selectedKey={filter}
            onSelectionChange={(k) => setFilter(k as FilterTab)}
          >
            {FILTER_TABS.map((t) => (
              <Tab key={t.key} title={t.label} />
            ))}
          </Tabs>
          <Input
            size="sm"
            placeholder="Search title, source, keywords, narrative…"
            value={searchText}
            onValueChange={setSearchText}
            isClearable
            onClear={() => setSearchText('')}
          />
        </CardBody>
      </Card>

      {loadError && (
        <div className="p-3 bg-danger-50 dark:bg-danger-950/30 text-danger-700 dark:text-danger-300 rounded text-sm">
          {loadError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardBody className="py-12 text-center text-default-500">
            {filter === 'unreviewed'
              ? 'Nothing left to review here. 🎉'
              : 'No records match this filter.'}
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Thumbnail queue */}
          <div className="lg:col-span-1 space-y-2 lg:max-h-[calc(100vh-8rem)] lg:overflow-auto">
            <div className="text-xs font-semibold text-default-500 uppercase tracking-wide">
              Queue ({visibleItems.length}
              {searchText.trim() ? ` of ${items.length}` : ''})
            </div>
            {visibleItems.length === 0 && (
              <p className="text-xs text-default-400 py-4 text-center">
                No records match &ldquo;{searchText.trim()}&rdquo;
              </p>
            )}
            {visibleItems.map((it, i) => (
              <button
                key={it.gifId}
                ref={(el) => {
                  if (el) queueButtonRefs.current.set(it.gifId, el)
                  else queueButtonRefs.current.delete(it.gifId)
                }}
                onClick={() => setActiveId(it.gifId)}
                className={`w-full flex gap-2 items-center p-2 rounded-lg text-left transition-colors ${
                  it.gifId === highlightId
                    ? 'bg-warning-100 dark:bg-warning-900/40 ring-2 ring-warning-400'
                    : it.gifId === activeId
                      ? 'bg-primary-100 dark:bg-primary-900/40 ring-1 ring-primary-300'
                      : 'bg-default-50 dark:bg-default-100/10 hover:bg-default-100'
                }`}
              >
                <div className="w-12 h-12 flex-shrink-0">
                  <ReviewGif item={it} className="w-12 h-12 rounded" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs line-clamp-1 font-medium">
                    {i + 1}. {it.gif?.title ?? it.gifId}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1">
                    {statusChip(it)}
                    {it.duplicateCandidates?.length ? (
                      <span
                        className="text-warning-500 text-xs"
                        title="Possible duplicate in the collection"
                        aria-label="Possible duplicate"
                      >
                        ⚠
                      </span>
                    ) : null}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Focused review card */}
          <div className="lg:col-span-3 space-y-4">
            {active && (
              <>
                {active.duplicateCandidates?.length ? (
                  <DuplicateWarningCard
                    candidates={active.duplicateCandidates}
                    onView={setActiveId}
                  />
                ) : null}
                <Card>
                  <CardHeader className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold truncate">
                        {active.gif?.title ?? active.gifId}
                      </span>
                      {statusChip(active)}
                      {isUnpublished(active) && (
                        <Chip size="sm" color="warning" variant="dot">
                          unpublished
                        </Chip>
                      )}
                    </div>
                    <span className="font-mono text-[10px] text-default-400 truncate">
                      {active.gifId}
                    </span>
                  </CardHeader>
                  <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <ReviewGif
                          item={active}
                          className="w-full max-h-96 rounded-lg bg-default-100"
                        />
                        {active.curatorHints && (
                          <div className="p-3 rounded-lg bg-warning-50 dark:bg-warning-950/20 border border-warning-200 dark:border-warning-800">
                            <div className="text-xs font-semibold text-warning-700 dark:text-warning-300 mb-1">
                              Curator hints (persist through re-analysis)
                            </div>
                            <p className="text-xs whitespace-pre-wrap">
                              {active.curatorHints}
                            </p>
                          </div>
                        )}
                        <p className="text-[11px] text-default-400">
                          Analyzed {formatIso(active.analyzedAt)}
                          {active.analysisModel
                            ? ` · ${active.analysisModel}`
                            : ''}
                          {active.reviewedAt
                            ? ` · reviewed ${formatIso(active.reviewedAt)}`
                            : ''}
                        </p>

                        {/* Confirmed catalog fields — sent with every approve.
                            Precedence: gifs-doc truth → proposedMeta → empty. */}
                        <Card className="border border-default-200">
                          <CardBody className="space-y-3">
                            <div className="text-xs font-semibold text-default-500 uppercase tracking-wide">
                              Catalog (confirmed on approve)
                            </div>
                            <Select
                              size="sm"
                              label="Rating"
                              selectedKeys={
                                confirmed.explicitRating
                                  ? [confirmed.explicitRating]
                                  : []
                              }
                              onSelectionChange={(keys) => {
                                const v = Array.from(keys)[0] as
                                  | ExplicitRating
                                  | undefined
                                setConfirmed((p) => ({
                                  ...p,
                                  explicitRating: v ?? '',
                                }))
                              }}
                            >
                              {RATING_OPTIONS.map((r) => (
                                <SelectItem key={r}>{r}</SelectItem>
                              ))}
                            </Select>
                            <Input
                              size="sm"
                              label="Reference source"
                              placeholder="e.g. The Office"
                              value={confirmed.source}
                              onValueChange={(v) =>
                                setConfirmed((p) => ({ ...p, source: v }))
                              }
                            />
                            <div>
                              <span className="text-xs text-default-500 block mb-1">
                                Reference keywords
                              </span>
                              <KeywordsInput
                                value={confirmed.keywords}
                                onChange={(kw) =>
                                  setConfirmed((p) => ({ ...p, keywords: kw }))
                                }
                              />
                            </div>
                            <Input
                              size="sm"
                              type="number"
                              label="Reference year"
                              placeholder="e.g. 2005"
                              value={confirmed.year}
                              onValueChange={(v) =>
                                setConfirmed((p) => ({ ...p, year: v }))
                              }
                            />
                          </CardBody>
                        </Card>
                      </div>
                      <div>{renderRecordBody(active)}</div>
                    </div>
                  </CardBody>
                </Card>

                {/* Comparison: previous vs new after a re-analysis / edit */}
                {previous && (
                  <Card className="border border-default-200">
                    <CardHeader className="pb-1">
                      <h3 className="text-sm font-semibold">
                        Before vs after (previous record on the left)
                      </h3>
                    </CardHeader>
                    <CardBody>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="opacity-70">
                          <Chip size="sm" variant="flat" className="mb-2">
                            previous
                          </Chip>
                          {renderRecordBody(previous)}
                        </div>
                        <div>
                          <Chip
                            size="sm"
                            variant="flat"
                            color="primary"
                            className="mb-2"
                          >
                            new
                          </Chip>
                          {renderRecordBody(active)}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {fixOpen && renderFixPanel()}
                {editOpen && renderEditPanel()}

                {/* Action bar */}
                <div className="flex flex-wrap items-center gap-2 sticky bottom-0 bg-background/80 backdrop-blur py-2">
                  <Button
                    color="success"
                    isLoading={busy && !fixOpen && !editOpen}
                    onPress={() => void handleApprove()}
                    isDisabled={busy || deleting}
                  >
                    Approve (a)
                  </Button>
                  <Button
                    color="primary"
                    variant="flat"
                    onPress={() => {
                      setEditOpen(false)
                      setFixOpen((o) => !o)
                    }}
                    isDisabled={busy || deleting}
                  >
                    Fix…
                  </Button>
                  <Button
                    variant="flat"
                    onPress={openEdit}
                    isDisabled={busy || deleting}
                  >
                    Edit fields
                  </Button>
                  <Button
                    variant="flat"
                    onPress={() => void handleCopyLink()}
                    isDisabled={busy || deleting}
                  >
                    Copy Link
                  </Button>
                  <Button
                    variant="light"
                    onPress={handleSkip}
                    isDisabled={busy || deleting}
                  >
                    Skip (s)
                  </Button>
                  {/* Danger action pushed to the far end (ml-auto) and given a
                      low-prominence light style so it can't be fat-fingered next
                      to Approve. Deletion stays an explicit, confirmed action. */}
                  <Button
                    size="sm"
                    variant="light"
                    color="danger"
                    className="ml-auto"
                    isLoading={deleting}
                    onPress={() => void handleDelete()}
                    isDisabled={busy || deleting}
                  >
                    Delete
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <QuickAddModal
        isOpen={addModal.isOpen}
        onOpenChange={addModal.onOpenChange}
        onSuccess={handleAddSuccess}
      />
    </div>
  )
}

/** Incoming ingestion queue: gifs still analyzing, plus failures to retry. */
function IncomingSection({
  incoming,
  error,
  retryingId,
  onRetry,
}: {
  incoming: GifDocument[]
  error: string | null
  retryingId: string | null
  onRetry: (gifId: string) => void
}) {
  if (error) {
    return (
      <div className="p-3 bg-danger-50 dark:bg-danger-950/30 text-danger-700 dark:text-danger-300 rounded text-sm">
        Incoming queue error: {error}
      </div>
    )
  }
  if (incoming.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-1">
        <h2 className="text-sm font-semibold">
          Incoming ({incoming.length}) — analyzing on upload
        </h2>
      </CardHeader>
      <CardBody className="space-y-2">
        {incoming.map((g) => {
          const failed = g.processingStatus === 'analysis_failed'
          return (
            <div
              key={g.id}
              className="flex items-center gap-3 p-2 rounded-lg bg-default-50 dark:bg-default-100/10"
            >
              <div className="w-12 h-12 flex-shrink-0">
                <IncomingThumb gifUrl={g.gifUrl} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-mono truncate">{g.id}</p>
                <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                  <Chip
                    size="sm"
                    variant="flat"
                    color={
                      failed
                        ? 'danger'
                        : g.processingStatus === 'analyzing'
                          ? 'primary'
                          : 'default'
                    }
                  >
                    {g.processingStatus ?? 'unknown'}
                  </Chip>
                  {failed && g.analysisError && (
                    <span className="text-[11px] text-danger-500 truncate max-w-md">
                      {g.analysisError}
                    </span>
                  )}
                </div>
              </div>
              {(failed || g.processingStatus === 'pending_analysis') && (
                <Button
                  size="sm"
                  color={failed ? 'danger' : 'default'}
                  variant="flat"
                  isLoading={retryingId === g.id}
                  onPress={() => onRetry(g.id)}
                >
                  Retry
                </Button>
              )}
            </div>
          )
        })}
      </CardBody>
    </Card>
  )
}

/** Small thumbnail for an incoming gif (resolves the storage url). */
function IncomingThumb({ gifUrl }: { gifUrl: string }) {
  const [url, setUrl] = useState('')
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    resolveGifUrl(gifUrl)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
  }, [gifUrl])
  if (failed)
    return (
      <div className="w-12 h-12 rounded bg-default-100 flex items-center justify-center text-[9px] text-default-400">
        no preview
      </div>
    )
  if (!url)
    return (
      <div className="w-12 h-12 rounded bg-default-100 flex items-center justify-center">
        <Spinner size="sm" />
      </div>
    )
  return (
    <HeroImage
      src={url}
      alt="incoming gif"
      radius="sm"
      classNames={{ wrapper: 'w-12 h-12', img: 'w-12 h-12 object-cover' }}
    />
  )
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
}

function formatIso(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}
